import React from "react";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const stats = [
  { value: "24/7", label: "Operational Visibility" },
  { value: "100%", label: "Workflow Traceability" },
  { value: "Role-Based", label: "Access Control" },
  { value: "Real-Time", label: "Shipment Monitoring" },
];

const features = [
  {
    title: "Shipment Lifecycle Tracking",
    desc: "Monitor every shipment stage from booking to delivery with centralized visibility and clearer coordination across teams.",
    icon: "🚚",
  },
  {
    title: "Document Management",
    desc: "Handle invoices, shipping documents, approvals, and operational files securely in one controlled workflow.",
    icon: "📂",
  },
  {
    title: "Billing & Invoice Operations",
    desc: "Simplify invoice creation, validation, and tracking while reducing delays and improving accountability.",
    icon: "💳",
  },
  {
    title: "Customer & Vendor Coordination",
    desc: "Manage customers, vendors, and partners with organized operational records and more reliable communication.",
    icon: "🤝",
  },
  {
    title: "Reporting & Analytics",
    desc: "Generate business reports and operational insights that help teams improve efficiency and decision-making.",
    icon: "📊",
  },
  {
    title: "User Access Management",
    desc: "Control access by role, responsibility, and workflow to reduce exposure and strengthen data protection.",
    icon: "🔐",
  },
];

const modules = [
  {
    title: "Operations Dashboard",
    desc: "A unified dashboard for shipment movement, pending actions, alerts, and workflow status.",
  },
  {
    title: "Finance & Billing",
    desc: "Support invoice handling, account visibility, and better coordination between operations and finance teams.",
  },
  {
    title: "Documentation Center",
    desc: "Keep shipping files, invoices, approvals, and critical records organized and easier to retrieve.",
  },
  {
    title: "Audit & Activity Logs",
    desc: "Track user activity and workflow actions for stronger monitoring, traceability, and compliance readiness.",
  },
];

const securityPoints = [
  "Role-based access control across departments and functions",
  "Controlled document access and secure operational workflows",
  "Safer API and system communication practices",
  "Audit logs for user actions and business events",
  "Structured permissions for sensitive business data",
  "Improved visibility into operational changes and approvals",
];

const useCases = [
  {
    title: "Freight Forwarding Teams",
    desc: "Coordinate daily shipment workflows with stronger operational clarity and fewer manual bottlenecks.",
  },
  {
    title: "Logistics Operations Managers",
    desc: "Improve shipment oversight, approvals, visibility, and reporting across distributed teams.",
  },
  {
    title: "Finance & Documentation Teams",
    desc: "Manage invoices and shipping documents with better control, consistency, and traceability.",
  },
];

const benefits = [
  {
    title: "Faster Operations",
    desc: "Reduce process delays by bringing workflows, documentation, and shipment visibility into one platform.",
  },
  {
    title: "Better Control",
    desc: "Improve how teams manage access, approvals, records, and operational accountability.",
  },
  {
    title: "Lower Risk",
    desc: "Support safer data handling and better oversight of actions across users, workflows, and documents.",
  },
  {
    title: "Scalable Growth",
    desc: "Use a platform designed to support business expansion without losing structure or visibility.",
  },
];

const Product = () => {
  return (
    <>
      {/* HERO */}
      <section className="container-fluid tw-bg-gradient-to-r tw-from-slate-950 tw-via-slate-900 tw-to-slate-800 tw-text-white tw-py-5">
        <div className="container">
          <div className="row align-items-center tw-min-h-[85vh]">
            <motion.div
              className="col-lg-6 mb-5 mb-lg-0"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.span
                variants={fadeUp}
                className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.25em] tw-text-cyan-300 tw-mb-3"
              >
                Logistics Product Platform
              </motion.span>

              <motion.h1
                className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-mb-3"
                variants={fadeUp}
              >
                Freight Forwarding Software Built for Secure, Efficient Operations
              </motion.h1>

              <motion.p
                className="tw-text-slate-300 tw-text-lg tw-mb-4"
                variants={fadeUp}
              >
                A modern logistics platform designed to manage shipments,
                documentation, billing workflows, and user operations with
                stronger control, visibility, and security.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="tw-flex tw-flex-wrap tw-gap-3 tw-mb-4"
              >
                <NavLink to="/contact" className="btn btn-primary">
                  Request Demo
                </NavLink>
                <NavLink to="/services" className="btn btn-outline-light">
                  Explore Services
                </NavLink>
              </motion.div>

              <motion.div
                variants={fadeUp}
                className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3"
              >
                <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-2xl tw-p-3">
                  <h6 className="tw-font-semibold tw-mb-1">Centralized Control</h6>
                  <p className="tw-text-sm tw-text-slate-300 tw-mb-0">
                    Manage core workflows from one system.
                  </p>
                </div>
                <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-2xl tw-p-3">
                  <h6 className="tw-font-semibold tw-mb-1">Secure Operations</h6>
                  <p className="tw-text-sm tw-text-slate-300 tw-mb-0">
                    Safer handling of users, records, and data.
                  </p>
                </div>
                <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-2xl tw-p-3">
                  <h6 className="tw-font-semibold tw-mb-1">Business Visibility</h6>
                  <p className="tw-text-sm tw-text-slate-300 tw-mb-0">
                    Better insight into shipments and actions.
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
            >
              <div className="tw-relative">
                <img
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d"
                  alt="Freight Software"
                  className="img-fluid tw-rounded-3xl tw-shadow-lg tw-w-full"
                />

                <div className="tw-absolute tw-bottom-4 tw-right-4 tw-bg-white tw-text-slate-900 tw-rounded-2xl tw-shadow-xl tw-p-4 tw-max-w-xs">
                  <h6 className="tw-font-bold tw-mb-1">Operational Intelligence</h6>
                  <p className="tw-text-sm tw-text-slate-600 tw-mb-0">
                    Bring shipment workflows, documents, billing, and team
                    actions together in one system.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="container tw-py-5">
        <motion.div
          className="row text-center"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {stats.map((item, i) => (
            <motion.div
              key={i}
              className="col-6 col-md-3 mb-4"
              variants={fadeUp}
            >
              <div className="tw-bg-white tw-rounded-3xl tw-p-4 tw-shadow-sm tw-border tw-border-slate-100">
                <h2 className="tw-text-3xl tw-font-bold tw-mb-1">{item.value}</h2>
                <p className="tw-text-slate-600 tw-mb-0">{item.label}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* PRODUCT OVERVIEW */}
      <section className="container tw-py-5">
        <div className="row align-items-center tw-min-h-[500px]">
          <motion.div
            className="col-lg-6 mb-4 mb-lg-0"
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978"
              alt="Operations dashboard"
              className="img-fluid tw-rounded-3xl tw-shadow-lg tw-w-full"
            />
          </motion.div>

          <motion.div
            className="col-lg-6"
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <span className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.2em] tw-text-cyan-600 tw-mb-2">
              Product Overview
            </span>

            <h2 className="tw-text-3xl tw-font-semibold tw-mb-3">
              Built for Real Logistics Operations
            </h2>

            <p className="tw-text-slate-600 tw-mb-3">
              This platform is designed for organizations that need more than
              simple tracking. It supports day-to-day freight forwarding
              operations with structured workflows, document handling, user
              permissions, and clearer cross-team coordination.
            </p>

            <p className="tw-text-slate-600 tw-mb-4">
              Instead of fragmented tools and disconnected records, teams get a
              more reliable operating environment where shipments, invoices,
              approvals, and operational updates stay connected.
            </p>

            <ul className="tw-text-slate-700 tw-space-y-2 tw-pl-4">
              <li>• Centralized shipment and workflow management</li>
              <li>• Stronger collaboration between operations and finance</li>
              <li>• More consistent document handling and approvals</li>
              <li>• Better visibility into actions, changes, and business events</li>
            </ul>

            <div className="tw-mt-4 tw-flex tw-gap-3 tw-flex-wrap">
              <NavLink to="/contact" className="btn btn-primary">
                Request Demo
              </NavLink>
              <NavLink to="/about" className="btn btn-outline-secondary">
                Learn About Us
              </NavLink>
            </div>
          </motion.div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="container-fluid tw-bg-slate-50 tw-py-5">
        <div className="container">
          <motion.div
            className="text-center mb-5"
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <h2 className="tw-text-3xl tw-font-semibold">Powerful Features for Daily Operations</h2>
            <p className="tw-text-slate-600 tw-max-w-3xl tw-mx-auto">
              Built to support freight forwarding teams with the tools they need
              to manage workflows, records, billing, and visibility at scale.
            </p>
          </motion.div>

          <motion.div
            className="row"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            {features.map((item, i) => (
              <motion.div key={i} className="col-md-6 col-lg-4 mb-4" variants={fadeUp}>
                <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full hover:tw-shadow-lg tw-transition-all">
                  <div className="tw-text-4xl tw-mb-3">{item.icon}</div>
                  <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                  <p className="tw-text-slate-600 tw-text-sm tw-mb-0">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PLATFORM MODULES */}
      <section className="container tw-py-5">
        <div className="text-center mb-5">
          <h2 className="tw-text-3xl tw-font-semibold">Core Platform Modules</h2>
          <p className="tw-text-slate-600 tw-max-w-3xl tw-mx-auto">
            Structured modules help teams manage daily logistics operations with
            greater consistency, accuracy, and visibility.
          </p>
        </div>

        <motion.div
          className="row"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {modules.map((item, i) => (
            <motion.div key={i} className="col-md-6 mb-4" variants={fadeUp}>
              <div className="tw-bg-gradient-to-br tw-from-slate-900 tw-to-slate-800 tw-text-white tw-rounded-3xl tw-p-4 tw-h-full tw-shadow-md">
                <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                <p className="tw-text-slate-300 tw-mb-0">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* SECURITY */}
      <section className="container-fluid tw-bg-slate-100 tw-py-5">
        <div className="container">
          <div className="row align-items-center">
            <motion.div
              className="col-lg-5 mb-4 mb-lg-0"
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <h2 className="tw-text-3xl tw-font-semibold tw-mb-3">
                Built-in Security That Supports Business Control
              </h2>
              <p className="tw-text-slate-600">
                The platform is designed to improve operational protection
                without adding unnecessary complexity. Access, activity, and
                sensitive workflows are structured for safer execution.
              </p>
            </motion.div>

            <motion.div
              className="col-lg-7"
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="row">
                {securityPoints.map((item, i) => (
                  <div className="col-md-6 mb-3" key={i}>
                    <div className="tw-bg-white tw-p-3 tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
                      <span className="tw-text-cyan-600 tw-font-semibold">✔ </span>
                      <span className="tw-text-slate-700">{item}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container tw-py-5">
        <div className="text-center mb-5">
          <h2 className="tw-text-3xl tw-font-semibold">How the Platform Works</h2>
          <p className="tw-text-slate-600">
            A simple operating flow that helps teams move faster with more control.
          </p>
        </div>

        <motion.div
          className="row text-center"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {[
            { step: "01", title: "Create & Organize Shipments" },
            { step: "02", title: "Manage Documents & Billing" },
            { step: "03", title: "Track Progress & Operations" },
            { step: "04", title: "Review Reports & Activities" },
          ].map((item, i) => (
            <motion.div key={i} className="col-md-3 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
                <div className="tw-text-cyan-600 tw-font-bold tw-text-2xl tw-mb-2">
                  {item.step}
                </div>
                <h6 className="tw-font-semibold tw-mb-0">{item.title}</h6>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* USE CASES */}
      <section className="container-fluid tw-bg-slate-50 tw-py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="tw-text-3xl tw-font-semibold">Who This Product Is For</h2>
            <p className="tw-text-slate-600 tw-max-w-3xl tw-mx-auto">
              Designed for organizations that need better structure, visibility,
              and control across freight forwarding and logistics operations.
            </p>
          </div>

          <motion.div
            className="row"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            {useCases.map((item, i) => (
              <motion.div key={i} className="col-md-4 mb-4" variants={fadeUp}>
                <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
                  <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                  <p className="tw-text-slate-600 tw-mb-0">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* BUSINESS BENEFITS */}
      <section className="container tw-py-5">
        <div className="text-center mb-5">
          <h2 className="tw-text-3xl tw-font-semibold">Business Outcomes</h2>
          <p className="tw-text-slate-600">
            More than features — the platform supports stronger day-to-day execution.
          </p>
        </div>

        <motion.div
          className="row"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {benefits.map((item, i) => (
            <motion.div key={i} className="col-md-6 col-lg-3 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
                <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                <p className="tw-text-slate-600 tw-text-sm tw-mb-0">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
      <section className="container-fluid tw-bg-gradient-to-r tw-from-slate-950 tw-to-slate-800 tw-text-white tw-py-5 text-center">
        <div className="container">
          <h2 className="tw-text-3xl tw-font-semibold">
            Ready to Modernize Your Logistics Operations?
          </h2>
          <p className="tw-text-slate-300 tw-max-w-2xl tw-mx-auto">
            Explore how our freight forwarding platform can improve visibility,
            control, security, and business efficiency across your daily operations.
          </p>

          <div className="tw-mt-4 tw-flex tw-justify-center tw-gap-3 tw-flex-wrap">
            <NavLink to="/contact" className="btn btn-primary">
              Request Demo
            </NavLink>
            <NavLink to="/contact" className="btn btn-outline-light">
              Contact Us
            </NavLink>
          </div>
        </div>
      </section>
    </>
  );
};

export default Product;